import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Tooltip, Switch, FormControlLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getEffectiveValue } from '../../utils/receiptsUtils';

function SpendingThisMonth({ 
  selectedMonth, 
  selectedYear, 
  receipts,
  chartOptions, 
  handleMonthChange,
  formatDate,
}) {
  const [totalSpending, setTotalSpending] = useState(0);
  const [categoryData, setCategoryData] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState({});
  const [categoryHierarchy, setCategoryHierarchy] = useState({});
  const [useDetailedCategories, setUseDetailedCategories] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [monthOptions, setMonthOptions] = useState([]);

  // Generate month options for the last 12 months plus current month
  useEffect(() => {
    const generateMonthOptions = () => {
      const options = [];
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      // Add current month and previous 12 months
      for (let i = 0; i < 13; i++) {
        let month = currentMonth - i;
        let year = currentYear;

        // If month goes below 0, adjust month and year
        if (month < 0) {
          month = 12 + month; // month is negative, so adding it to 12 gives us the correct previous month
          year = year - 1;
        }

        options.push({
          month: month,
          year: year,
          label: new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(year, month))
        });
      }
      setMonthOptions(options);

      // Always initialize to the most recent month
      if (options.length > 0) {
        const mostRecent = options[0];
        handleMonthChange(mostRecent.month, mostRecent.year);
      }
    };

    generateMonthOptions();
  }, []); // Only run once on mount

  useEffect(() => {
    setIsLoading(true);
    
    Promise.all([
      fetchCategoryHierarchy(),
      processReceiptData(receipts)
    ]).finally(() => {
      setIsLoading(false);
    });
  }, [receipts, selectedMonth, selectedYear, useDetailedCategories]);

  const fetchCategoryHierarchy = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/item-categories`);
      const categories = response.data.categories;
      const hierarchy = {};
      categories.forEach(category => {
        hierarchy[category.name] = {
          emoji: category.emoji,
          subcategories: {}
        };
        category.subcategories.forEach(subcategory => {
          hierarchy[category.name].subcategories[subcategory.name] = subcategory.emoji;
        });
      });
      setCategoryHierarchy(hierarchy);
    } catch (error) {
      console.error('Error fetching category hierarchy:', error);
    }
  };

  const getEmojiForCategory = (category, subcategory) => {
    if (subcategory && categoryHierarchy[category]?.subcategories[subcategory]) {
      return categoryHierarchy[category].subcategories[subcategory] || '🧾';
    }
    return categoryHierarchy[category]?.emoji || '🧾';
  };

  const processReceiptData = async (receipts) => {
    try {
      console.log('Processing receipts:', receipts);
      
      const thisMonthReceipts = receipts.filter(receipt => {
        const receiptDate = new Date(getEffectiveValue(receipt, 'date') || getEffectiveValue(receipt, 'receipt_date'));
        return receiptDate.getMonth() === selectedMonth && receiptDate.getFullYear() === selectedYear;
      });

      console.log('Selected month receipts:', thisMonthReceipts);

      const total = thisMonthReceipts.reduce((sum, receipt) => 
        sum + parseFloat(getEffectiveValue(receipt, 'total_cost') || getEffectiveValue(receipt, 'receipt_total')), 0);
      setTotalSpending(total);

      // Fetch items for each receipt
      const allItems = await Promise.all(thisMonthReceipts.map(async (receipt) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/get_receipt_items?receipt_id=${receipt.uuid}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          // Add receipt date and merchant name to each item
          return response.data ? response.data.map(item => ({
            ...item,
            date: getEffectiveValue(receipt, 'date') || getEffectiveValue(receipt, 'receipt_date'),
            merchant_name: getEffectiveValue(receipt, 'merchant_name') || getEffectiveValue(receipt, 'store_name') || 'Unknown'
          })) : [];
        } catch (error) {
          console.error('Error fetching items for receipt:', receipt.uuid, error);
          return [];
        }
      }));

      // Filter out any null or undefined results and flatten the array
      const flattenedItems = allItems.filter(items => Array.isArray(items)).flat();
      
      if (flattenedItems.length === 0) {
        console.log('No items found for the selected month');
        setCategoryData(null);
        setCategoryDetails({});
        return;
      }

      // Process items by category
      const categoryTotals = {};
      const categoryDetails = {};

      flattenedItems.forEach(item => {
        const category = getEffectiveValue(item, 'high_level_category') || 'Uncategorized';
        const subcategory = useDetailedCategories ? (getEffectiveValue(item, 'detailed_category') || 'Uncategorized') : null;
        const categoryKey = useDetailedCategories ? subcategory : category;
        const price = parseFloat(getEffectiveValue(item, 'item_price')) || 0;
        
        if (!categoryTotals[categoryKey]) {
          categoryTotals[categoryKey] = 0;
          categoryDetails[categoryKey] = [];
        }
        
        categoryTotals[categoryKey] += price;
        categoryDetails[categoryKey].push({
          itemName: getEffectiveValue(item, 'item_name'),
          price: price,
          vendor: item.merchant_name,
          date: item.date,
          highLevelCategory: category,
          detailedCategory: getEffectiveValue(item, 'detailed_category'),
          categoryEmoji: getEmojiForCategory(category, getEffectiveValue(item, 'detailed_category'))
        });
      });

      // Sort categories by total amount (descending)
      const sortedCategories = Object.keys(categoryTotals).sort((a, b) => categoryTotals[b] - categoryTotals[a]);
      
      // Prepare data for the chart using the sorted categories
      const labels = sortedCategories;
      const data = labels.map(label => categoryTotals[label]);
      const backgroundColors = labels.map((_, index) => `hsl(${index * 360 / labels.length}, 70%, 50%)`);

      // Create a mapping of category keys to their display labels with emojis
      const categoryLabelMap = {};
      labels.forEach(label => {
        const category = useDetailedCategories ? 
          Object.keys(categoryHierarchy).find(cat => 
            categoryHierarchy[cat]?.subcategories[label]
          ) || label : 
          label;
        
        categoryLabelMap[label] = `${getEmojiForCategory(category, label)} ${label}`;
      });

      setCategoryData({
        labels: labels.map(label => categoryLabelMap[label]),
        datasets: [{
          data,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors.map(color => color.replace('50%', '40%')),
          borderWidth: 1,
        }],
      });

      // Store the category details with their totals
      setCategoryDetails(categoryDetails);
    } catch (error) {
      console.error('Error processing receipt data:', error);
    }
  };

  const handleToggleDetailedCategories = () => {
    setUseDetailedCategories(!useDetailedCategories);
  };

  return (
    <div>
      <div className="mb-4">
        <Select
          value={selectedMonth !== undefined && selectedYear !== undefined ? 
            JSON.stringify({
              month: selectedMonth,
              year: selectedYear,
              label: new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(selectedYear, selectedMonth))
            }) : monthOptions.length > 0 ? JSON.stringify(monthOptions[0]) : ''}
          onChange={(e) => {
            const selectedOption = JSON.parse(e.target.value);
            handleMonthChange(selectedOption.month, selectedOption.year);
          }}
          style={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            height: '32px',
            fontSize: '0.875rem',
          }}
          fullWidth
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: '#1F2937',
                color: 'white',
              },
            },
          }}
        >
          {monthOptions.map((option, index) => (
            <MenuItem 
              key={index} 
              value={JSON.stringify(option)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="text-4xl font-bold mb-4">${totalSpending.toFixed(2)}</div>

      <div className="mb-4 flex justify-center">
        <FormControlLabel
          control={
            <Switch
              checked={useDetailedCategories}
              onChange={handleToggleDetailedCategories}
              color="primary"
            />
          }
          label="Use Detailed Categories"
          style={{ color: 'white', margin: 0 }}
        />
      </div>

      <div className="h-80 w-full bg-gray-700 rounded-lg flex items-center justify-center mb-4">
        {isLoading ? (
          <CircularProgress />
        ) : categoryData ? (
          <Pie data={categoryData} options={{
            ...chartOptions,
            plugins: {
              ...chartOptions?.plugins,
              legend: {
                ...chartOptions?.plugins?.legend,
                position: 'right',
                labels: {
                  ...chartOptions?.plugins?.legend?.labels,
                  boxWidth: 15,
                  padding: 10,
                  font: {
                    size: 12
                  }
                }
              }
            }
          }} />
        ) : (
          <p className="text-white">
            No spending data for {selectedMonth !== undefined && selectedYear !== undefined ? 
              new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(selectedYear, selectedMonth)) :
              monthOptions.length > 0 ? monthOptions[0].label : 'the selected period'}
          </p>
        )}
      </div>
      {/* Category Details Accordions */}
      {Object.entries(categoryDetails)
        .sort(([, itemsA], [, itemsB]) => {
          const totalA = itemsA.reduce((sum, item) => sum + item.price, 0);
          const totalB = itemsB.reduce((sum, item) => sum + item.price, 0);
          return totalB - totalA;
        })
        .map(([category, items]) => {
          // Calculate the total directly from the items
          const categoryTotal = items.reduce((sum, item) => sum + item.price, 0);
          
          // Find the corresponding label in the chart data for display purposes
          const categoryLabel = categoryData?.labels.find(label => 
            label.includes(category) || 
            (useDetailedCategories && categoryHierarchy[category]?.subcategories[category])
          ) || `${getEmojiForCategory(category)} ${category}`;
          
          return (
            <Accordion key={category} className="bg-gray-700 text-white mb-2">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon className="text-white" />}
                aria-controls={`${category}-content`}
                id={`${category}-header`}
              >
                <Typography>
                  {categoryLabel} - ${categoryTotal.toFixed(2)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul className="space-y-4">
                  {items.map((item, index) => (
                    <li key={index} className="flex flex-col sm:grid sm:grid-cols-[2fr,2fr,1fr] sm:gap-4 sm:items-center">
                      {/* Top row on mobile: Item name and price */}
                      <div className="flex justify-between items-center">
                        <Tooltip title={`${item.highLevelCategory} > ${item.detailedCategory}`} arrow>
                          <span className="truncate text-left mr-2">
                            {item.categoryEmoji} {item.itemName}
                          </span>
                        </Tooltip>
                        <span className="font-medium sm:hidden">
                          ${item.price.toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Bottom row on mobile: Merchant and date */}
                      <div className="text-sm text-gray-300 sm:justify-self-end">
                        <span className="sm:hidden">{item.vendor} - {formatDate(item.date)}</span>
                        <span className="hidden sm:inline">{item.vendor} - {formatDate(item.date)}</span>
                      </div>
                      
                      {/* Price - only visible on desktop */}
                      <span className="hidden sm:block text-right font-medium">
                        ${item.price.toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>
          );
        })}
    </div>
  );
}

export default SpendingThisMonth;
